import React, { useState } from "react";
import {
  Select,
  Typography,
  Card,
  Spin,
  message,
  Tag,
  Button,
  FloatButton,
} from "antd";
import {
  HomeOutlined,
  HeartOutlined,
  BankOutlined,
  IdcardOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  RobotOutlined,
  VerticalAlignTopOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import type { EducationalInstitution } from "../data";
import { institutions } from "../data";

const { Title, Text } = Typography;
const { Option } = Select;

const services = [
  {
    key: "tim-nha",
    label: "Tìm Nhà & KTX",
    icon: <HomeOutlined />,
    description: "Hỗ trợ tìm nhà ở, căn hộ an ninh gần trường học.",
  },
  {
    key: "kham-suc-khoe",
    label: "Khám Sức Khỏe",
    icon: <HeartOutlined />,
    description: "Hướng dẫn mua bảo hiểm & khám y tế chuẩn Visa.",
  },
  {
    key: "lam-the-bank",
    label: "Thẻ Ngân Hàng",
    icon: <BankOutlined />,
    description: "Thủ tục mở tài khoản debit/credit cho du học sinh.",
  },
  {
    key: "lam-visa",
    label: "Thủ Tục Visa",
    icon: <IdcardOutlined />,
    description: "Cập nhật luật cư trú, gia hạn Visa & giấy tờ.",
  },
];

const CyprusConsultant: React.FC = () => {
  const [selectedSchool, setSelectedSchool] =
    useState<EducationalInstitution | null>(null);
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [result, setResult] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleSchoolChange = (value: string) => {
    const school = institutions.find((inst) => inst.name === value) || null;
    setSelectedSchool(school);
    setSelectedService(null);
    setResult("");
  };

  const handleServiceClick = async (serviceKey: string) => {
    if (!selectedSchool) {
      message.warning("Vui lòng chọn trường đại học của bạn trước nhé!");
      return;
    }

    setSelectedService(serviceKey);
    setLoading(true);
    setResult("");

    const serviceInfo = services.find((s) => s.key === serviceKey);
    const prompt = `Bạn là chuyên gia tư vấn du học đảo Síp (Cyprus) dành riêng cho sinh viên Việt Nam.
Sinh viên đang học tại: ${selectedSchool.name}
Vị trí: ${selectedSchool.locations.join(", ")}
Website trường: ${selectedSchool.url}

Yêu cầu hỗ trợ cụ thể: ${serviceInfo?.label} - ${serviceInfo?.description}

Hãy trả lời CHI TIẾT, THỰC TẾ, bằng tiếng Việt, bao gồm:
- Các bước thực hiện cụ thể
- Chi phí tham khảo mới nhất (nếu có)
- Địa chỉ / liên hệ hữu ích gần trường
- Mẹo dành cho sinh viên Việt Nam
- Lưu ý quan trọng (visa, giấy tờ, thời gian)

Sử dụng thông tin mới nhất từ Tavily API để đảm bảo tính chính xác năm 2026.`;

    try {
      const response = await axios.post(
        "https://groqprompt.netlify.app/api/cy",
        { prompt },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        },
      );
      setResult(response.data.result || response.data);
    } catch (error: any) {
      console.error(error);
      const errorMsg = error.response?.data?.error || error.message;
      setResult(
        `❌ **Lỗi khi gọi API:** ${errorMsg}\n\nVui lòng thử lại sau giây lát.`,
      );
      message.error("Hệ thống AI đang bận. Vui lòng thử lại!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ maxWidth: 900, margin: "0 auto" }}
      className="animate-fade-in"
    >
      {/* STEP 1: CHỌN TRƯỜNG */}
      <div style={{ marginBottom: "32px" }}>
        <Title
          level={4}
          style={{
            color: "#64748b",
            textTransform: "uppercase",
            fontSize: "14px",
            letterSpacing: "1px",
          }}
        >
          Bước 1
        </Title>
        <Title level={2} style={{ marginTop: 0, marginBottom: "24px" }}>
          Chọn điểm đến của bạn 🎓
        </Title>

        <Card
          bordered={false}
          style={{
            borderRadius: "16px",
            boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
          }}
        >
          <Select
            placeholder="Tìm kiếm trường đại học / cao đẳng..."
            optionFilterProp="children"
            onChange={handleSchoolChange}
            style={{ width: "100%" }}
            size="large"
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {institutions.map((inst) => (
              <Option key={inst.name} value={inst.name}>
                {inst.name} ({inst.locations.join(", ")})
              </Option>
            ))}
          </Select>

          {selectedSchool && (
            <div
              className="animate-slide-up"
              style={{
                marginTop: "20px",
                padding: "16px",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Text
                strong
                style={{
                  fontSize: "18px",
                  color: "#0f172a",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                {selectedSchool.name}
              </Text>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Tag icon={<EnvironmentOutlined />} color="blue">
                  {selectedSchool.locations.join(", ")}
                </Tag>
                <Tag icon={<GlobalOutlined />} color="cyan">
                  <a
                    href={selectedSchool.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "inherit" }}
                  >
                    Website chính thức
                  </a>
                </Tag>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* STEP 2: CHỌN DỊCH VỤ */}
      <div
        style={{
          opacity: selectedSchool ? 1 : 0.5,
          transition: "opacity 0.3s ease",
          pointerEvents: selectedSchool ? "auto" : "none",
        }}
      >
        <Title
          level={4}
          style={{
            color: "#64748b",
            textTransform: "uppercase",
            fontSize: "14px",
            letterSpacing: "1px",
          }}
        >
          Bước 2
        </Title>
        <Title level={2} style={{ marginTop: 0, marginBottom: "24px" }}>
          Bạn cần hỗ trợ gì hôm nay? 🛠️
        </Title>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          {services.map((service) => (
            <div
              key={service.key}
              className={`service-card ${selectedService === service.key ? "active" : ""}`}
              onClick={() =>
                handleSchoolChange && handleServiceClick(service.key)
              }
            >
              <div className="service-icon">{service.icon}</div>
              <Text strong style={{ fontSize: "16px", color: "#0f172a" }}>
                {service.label}
              </Text>
              <Text
                style={{ fontSize: "13px", color: "#64748b", lineHeight: 1.5 }}
              >
                {service.description}
              </Text>
            </div>
          ))}
        </div>
      </div>

      {/* RESULT SECTION */}
      {loading && (
        <Card
          bordered={false}
          style={{
            borderRadius: "16px",
            textAlign: "center",
            padding: "40px 0",
          }}
        >
          <Spin size="large" />
          <Text
            style={{
              display: "block",
              marginTop: "16px",
              color: "#64748b",
              fontSize: "16px",
            }}
          >
            AI đang phân tích dữ liệu mới nhất năm 2026 cho bạn...
          </Text>
        </Card>
      )}

      {result && !loading && (
        <Card
          bordered={false}
          className="animate-slide-up"
          style={{
            borderRadius: "16px",
            boxShadow: "0 20px 40px -10px rgba(2, 132, 199, 0.1)",
            borderTop: "4px solid #0284c7",
          }}
          title={
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "18px",
              }}
            >
              <RobotOutlined style={{ color: "#0284c7" }} />
              <span style={{ fontWeight: 700, color: "#0f172a" }}>
                Tư vấn tự động
              </span>
            </div>
          }
          extra={
            <Button
              type="text"
              onClick={() => {
                setResult("");
                setSelectedService(null);
              }}
            >
              Làm mới
            </Button>
          }
        >
          <div className="markdown-wrapper">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {result}
            </ReactMarkdown>
          </div>
        </Card>
      )}
      <FloatButton
        icon={<VerticalAlignTopOutlined />}
        style={{ right: 24, bottom: 24 }}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      />
    </div>
  );
};

export default CyprusConsultant;
